import React, { useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, useReducedMotion } from "framer-motion";
import { HashLink } from "react-router-hash-link";

import Pizza from "../../stuff/onboard5.png";
import Salad from "../../stuff/onboard4.png";
import Delivery from "../../stuff/onboard6.png";
import vendor from "../../stuff/vendor.png";

const PRIMARY = "#0c513f";
const ACCENT = "#ffbf07";
const CREAM = "#fdf7e9";

const FoodDoodle = ({ style = {}, stroke = "rgba(255,255,255,0.20)" }) => (
  <svg
    width="520"
    height="520"
    viewBox="0 0 520 520"
    fill="none"
    style={{ position: "absolute", ...style }}
    aria-hidden="true"
  >
    {/* Bowl */}
    <path
      d="M120 278c24 88 88 140 140 140s116-52 140-140"
      stroke={stroke}
      strokeWidth="10"
      strokeLinecap="round"
    />
    <path d="M150 278h220" stroke={stroke} strokeWidth="10" strokeLinecap="round" />
    {/* Steam */}
    <path d="M210 182c-10 16-10 30 0 46" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
    <path d="M260 168c-12 18-12 34 0 52" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
    <path d="M310 182c-10 16-10 30 0 46" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
    {/* Sparkle */}
    <path
      d="M94 140l10 22 22 10-22 10-10 22-10-22-22-10 22-10 10-22z"
      stroke={stroke}
      strokeWidth="7"
      strokeLinejoin="round"
    />
    {/* Fork */}
    <path d="M420 170v70M445 170v70M432 170v88" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
    <path d="M432 258v90" stroke={stroke} strokeWidth="8" strokeLinecap="round" />
  </svg>
);

const Sparkle = ({ style = {}, delay = 0 }) => (
  <motion.div
    style={{
      position: "absolute",
      width: 8,
      height: 8,
      borderRadius: 999,
      background: "rgba(255,191,7,0.95)",
      boxShadow: "0 0 18px rgba(255,191,7,0.55)",
      ...style,
    }}
    initial={{ opacity: 0, scale: 0.7 }}
    animate={{
      opacity: [0, 0.9, 0],
      scale: [0.7, 1.15, 0.7],
      y: [0, -12, 0],
    }}
    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

const mockData = [
  {
    image: Pizza,
    title: "Chow Now, Pay Later",
    tag: "Flexible",
    emoji: "💳",
    paragraph:
      "Enjoy your favourite meal without the wait. Dig into your jollof, suya, or any delicious treat right away, and sort the bill when it suits you. Good food, good vibes, zero stress.",
  },
  {
    image: Delivery,
    title: "Chow Shuffle",
    tag: "Surprise me",
    emoji: "🎲",
    paragraph:
      "Stuck choosing your next meal? Tap Chow Shuffle and let the app surprise you with a tasty pick. No overthinking — just pure flavour and a fun little adventure every time.",
  },
  {
    image: Salad,
    title: "Chow Schedule",
    tag: "On your time",
    emoji: "⏱️",
    paragraph:
      "Pick the perfect delivery time and we’ll handle the rest. Set a slot for lunch or dinner and your food arrives right on cue — smooth, simple, and reliable.",
  },
];

function Section2() {
  const reduceMotion = useReducedMotion();

  const sparkleLayout = useMemo(
    () => [
      { top: "18%", left: "9%", delay: 0.1 },
      { top: "32%", left: "18%", delay: 0.65 },
      { top: "16%", right: "18%", delay: 0.25 },
      { top: "40%", right: "10%", delay: 0.9 },
      { bottom: "22%", left: "14%", delay: 0.45 },
      { bottom: "16%", right: "22%", delay: 0.75 },
    ],
    []
  );

  return (
    <>
      {/* ====== Vendor / Earn section ====== */}
      <section
        className="about_section"
        style={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(120deg, ${PRIMARY}, #0b4a3a, ${PRIMARY})`,
          padding: "78px 0 72px",
        }}
      >
        {/* ambient layers */}
        <motion.div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(1100px 700px at 20% 10%, rgba(255,191,7,0.20), transparent 60%)," +
              "radial-gradient(800px 600px at 85% 25%, rgba(255,255,255,0.10), transparent 55%)," +
              "radial-gradient(900px 520px at 40% 92%, rgba(255,191,7,0.14), transparent 55%)",
            opacity: 1,
          }}
          animate={reduceMotion ? undefined : { backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={reduceMotion ? undefined : { duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* doodles */}
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, opacity: 0.85 }}>
          <FoodDoodle style={{ top: "-140px", right: "-140px", transform: "rotate(10deg)" }} />
          <FoodDoodle
            style={{ bottom: "-210px", left: "-190px", transform: "rotate(-12deg) scale(0.9)", opacity: 0.7 }}
            stroke="rgba(255,191,7,0.18)"
          />
          {sparkleLayout.map((s, i) => (
            <Sparkle key={i} delay={s.delay} style={s} />
          ))}
        </div>

        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row className="align-items-center g-5">
            {/* image */}
            <Col lg={6} className="text-center">
              <motion.div
                style={{
                  position: "relative",
                  maxWidth: 520,
                  margin: "0 auto",
                  borderRadius: 28,
                  padding: 14,
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  boxShadow: "0 30px 90px rgba(0,0,0,0.28)",
                  overflow: "hidden",
                }}
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* sheen sweep */}
                <motion.div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: "-70%",
                    left: "-40%",
                    width: "40%",
                    height: "240%",
                    background: "rgba(255,255,255,0.16)",
                    transform: "rotate(18deg)",
                    filter: "blur(2px)",
                  }}
                  animate={reduceMotion ? undefined : { x: ["-10%", "220%"] }}
                  transition={reduceMotion ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />

                <motion.img
                  src={vendor}
                  className="img-fluid d-block"
                  alt="Vendor"
                  style={{
                    width: "100%",
                    borderRadius: 22,
                    border: "1px solid rgba(255,191,7,0.22)",
                  }}
                  initial={{ opacity: 0, scale: 0.98 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={
    reduceMotion
      ? { duration: 0.8, ease: "easeOut", delay: 0.1 }
      : {
          opacity: { duration: 0.8, ease: "easeOut", delay: 0.1 },
          scale: { duration: 0.8, ease: "easeOut", delay: 0.1 },
          y: { duration: 4.2, repeat: Infinity, ease: "easeInOut" },
        }
  }
                  viewport={{ once: true }}
                  animate={
                    reduceMotion
                      ? undefined
                      : {
                          y: [0, -6, 0],
                        }
                  }
                  // small loop for premium feel
                  // eslint-disable-next-line
                 
                />
              </motion.div>
            </Col>

            {/* copy */}
            <Col lg={6} className="text-center text-lg-start">
              {/* pill */}
              <motion.div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                  marginBottom: 16,
                }}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: ACCENT,
                    boxShadow: "0 0 18px rgba(255,191,7,0.7)",
                  }}
                />
               
              </motion.div>

              <motion.h2
                className="text-white"
                style={{
                  fontSize: "clamp(30px, 3.4vw, 44px)",
                  lineHeight: 1.06,
                  letterSpacing: "-0.4px",
                  marginBottom: 14,
                  textShadow: "0 18px 60px rgba(0,0,0,0.30)",
                }}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.05 }}
                viewport={{ once: true }}
              >
                From your <span style={{ color: ACCENT }}>kitchen</span> to your wallet
              </motion.h2>

              {/* glass paragraph */}
              <motion.div
                style={{
                  borderRadius: 22,
                  padding: 18,
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,191,7,0.28)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  boxShadow: "0 22px 70px rgba(0,0,0,0.22)",
                  position: "relative",
                  overflow: "hidden",
                }}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.12 }}
                viewport={{ once: true }}
              >
                <motion.div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: "-60%",
                    left: "-40%",
                    width: "40%",
                    height: "240%",
                    background: "rgba(255,255,255,0.14)",
                    transform: "rotate(18deg)",
                    filter: "blur(2px)",
                  }}
                  animate={reduceMotion ? undefined : { x: ["-10%", "220%"] }}
                  transition={reduceMotion ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />

                <p
                  className="text-white"
                  style={{
                    margin: 0,
                    fontSize: "clamp(15px, 1.15vw, 17px)",
                    lineHeight: 1.75,
                    color: "rgba(255,255,255,0.92)",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  Hey vendors — welcome to a smarter way to earn. Every order puts cash in your pocket while you keep doing what
                  you love: serving your signature dishes. Withdrawals are quick and clean, so you can focus on cooking magic.
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div
                style={{ marginTop: 18 }}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true }}
              >
                <HashLink
                  smooth
                  to="#features"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    textDecoration: "none",
                    padding: "14px 18px",
                    borderRadius: 999,
                    color: PRIMARY,
                    background: `linear-gradient(135deg, ${ACCENT}, #ffd45b, ${ACCENT})`,
                    border: "1px solid rgba(255,255,255,0.26)",
                    boxShadow: "0 22px 70px rgba(0,0,0,0.26)",
                    fontWeight: 800,
                    letterSpacing: "0.2px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <motion.span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: "-70%",
                      left: "-40%",
                      width: "40%",
                      height: "240%",
                      background: "rgba(255,255,255,0.35)",
                      transform: "rotate(18deg)",
                      filter: "blur(2px)",
                    }}
                    animate={reduceMotion ? undefined : { x: ["-10%", "220%"] }}
                    transition={reduceMotion ? undefined : { duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <span style={{ position: "relative", zIndex: 1 }}>
                    Dive below to see all the features
                  </span>
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(12,81,63,0.12)",
                      border: "1px solid rgba(12,81,63,0.22)",
                    }}
                  >
                    ➜
                  </span>
                </HashLink>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ====== Features section ====== */}
      <section
        className="shop-section"
        id="features"
        style={{
          position: "relative",
          overflow: "hidden",
          background: CREAM,
          padding: "72px 0",
          borderTop: "1px solid rgba(255,191,7,0.22)",
        }}
      >
        {/* subtle background flourishes */}
        <div aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
          <div
            style={{
              position: "absolute",
              top: "-160px",
              left: "-220px",
              width: 520,
              height: 520,
              borderRadius: 999,
              background: "rgba(255,191,7,0.18)",
              filter: "blur(70px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-200px",
              right: "-240px",
              width: 560,
              height: 560,
              borderRadius: 999,
              background: "rgba(12,81,63,0.10)",
              filter: "blur(80px)",
            }}
          />
        </div>

        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row className="justify-content-center text-center" style={{ marginBottom: 22 }}>
            <Col lg={8}>
              <motion.h3
                style={{
                  fontSize: "clamp(26px, 3vw, 36px)",
                  color: "#101010",
                  marginBottom: 10,
                  letterSpacing: "-0.3px",
                }}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Features that feel <span style={{ color: PRIMARY }}>effortless</span>
              </motion.h3>
              <motion.p
                style={{
                  margin: "0 auto",
                  maxWidth: 760,
                  color: "rgba(16,16,16,0.62)",
                  fontSize: 15,
                  lineHeight: 1.7,
                }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.05 }}
                viewport={{ once: true }}
              >
                Smooth flows, clean UI, and little touches that make ordering feel premium.
              </motion.p>
            </Col>
          </Row>

          <Row className="justify-content-md-center g-4">
            {mockData.map((cardData, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: "easeOut", delay: 0.08 * index }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  style={{
                    height: "100%",
                    borderRadius: 22,
                    padding: 18,
                    background: "rgba(255,255,255,0.78)",
                    border: "1px solid rgba(255,191,7,0.28)",
                    boxShadow: "0 18px 55px rgba(0,0,0,0.10)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* top accent bar */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: `linear-gradient(90deg, ${ACCENT}, rgba(255,191,7,0.15), ${ACCENT})`,
                      opacity: 0.9,
                    }}
                  />

                  {/* inner sheen */}
                  <motion.div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: "-70%",
                      left: "-45%",
                      width: "45%",
                      height: "240%",
                      background: "rgba(255,255,255,0.30)",
                      transform: "rotate(18deg)",
                      filter: "blur(2px)",
                      opacity: 0.55,
                    }}
                    animate={reduceMotion ? undefined : { x: ["-10%", "240%"] }}
                    transition={reduceMotion ? undefined : { duration: 7, repeat: Infinity, ease: "easeInOut" }}
                  />

                  {/* icon frame */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 12,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <div
                      style={{
                        width: 82,
                        height: 82,
                        borderRadius: 22,
                        background: "rgba(12,81,63,0.06)",
                        border: "1px solid rgba(12,81,63,0.12)",
                        display: "grid",
                        placeItems: "center",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                      }}
                    >
                      <img
                        src={cardData.image}
                        alt="icon"
                        style={{ width: 56, height: 56, objectFit: "contain" }}
                      />
                    </div>

                    {/* tiny tag pill */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: -6,
                        padding: "7px 10px",
                        borderRadius: 999,
                        background: "rgba(255,191,7,0.14)",
                        border: "1px solid rgba(255,191,7,0.30)",
                        color: PRIMARY,
                        fontWeight: 800,
                        fontSize: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span>{cardData.emoji}</span>
                      <span>{cardData.tag}</span>
                    </div>
                  </div>

                  <h4 style={{ position: "relative", zIndex: 1, color: "#101010", marginTop: 10 }}>
                    {cardData.title}
                  </h4>

                  <div
                    aria-hidden="true"
                    style={{
                      width: 88,
                      height: 5,
                      borderRadius: 999,
                      background: PRIMARY,
                      opacity: 0.9,
                      margin: "10px auto 12px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />

                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      padding: 14,
                      borderRadius: 16,
                      background: "rgba(255,191,7,0.10)",
                      borderLeft: `4px solid ${ACCENT}`,
                      borderRight: `4px solid ${ACCENT}`,
                    }}
                  >
                    <p style={{ fontSize: 14, lineHeight: 1.9, margin: 0, color: "rgba(16,16,16,0.62)" }}>
                      {cardData.paragraph}
                    </p>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  );
}

export default Section2;
